import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const useUserStore = create(
    persist(
        (set, get) => ({
            userId: null,
            user: null,
            blogUrl: '',
            blogs: [],
            drafts: {},
            keywordData: {},
            loading: false,
            error: null,
            sortedArticles: [],
            filteredArticles: [],
            aggregatedMetrics: {},
            selectedStatus: '',
            selectedType: '',
            selectedDate: '',
            hasSeenTutorial: false,
            tutorialStarted: false,
            processingTasks: {},
            sortConfig: { key: 'date', direction: 'ascending' }, // Add sortConfig
            subscriptionEndDate: null, // Add subscriptionEndDate to store
            hasFetchedDrafts: false, // ADD THIS LINE

            setUserId: (userId) => set({ userId }),
            setUser: (user) => set({ user }),
            setBlogUrl: (blogUrl) => set({ blogUrl }),
            setBlogs: (blogs) => set({ blogs }),
            setDrafts: (drafts) => set({ drafts }),
            setKeywordData: (keywordData) => set({ keywordData }),
            setLoading: (loading) => set({ loading }),
            setError: (error) => set({ error }),
            setSortedArticles: (sortedArticles) => set({ sortedArticles }),
            setFilteredArticles: (filteredArticles) => set({ filteredArticles }),
            setAggregatedMetrics: (aggregatedMetrics) => set({ aggregatedMetrics }),
            setSelectedStatus: (selectedStatus) => set({ selectedStatus }),
            setSelectedType: (selectedType) => set({ selectedType }),
            setSelectedDate: (selectedDate) => set({ selectedDate }),
            setHasSeenTutorial: (hasSeenTutorial) => set({ hasSeenTutorial }),
            setTutorialStarted: (tutorialStarted) => set({ tutorialStarted }),
            setProcessingTasks: (processingTasks) => set({ processingTasks }),
            setSortConfig: (sortConfig) => set({ sortConfig }), // Add setSortConfig
            setSubscriptionEndDate: (subscriptionEndDate) => set({ subscriptionEndDate }), // Add setSubscriptionEndDate
            setHasFetchedDrafts: (hasFetchedDrafts) => set({ hasFetchedDrafts }), // ADD THIS LINE

            // Add selectors for sorted/filtered articles and aggregated metrics
            getSortedArticles: () => {
                const { blogs, drafts, sortConfig } = get();
                let articles = Object.values(drafts).flat().map(draft => ({
                    ...draft,
                    blog: blogs.find(blog => blog.id === draft.blogId),
                    date: new Date(draft.date),
                }));

                articles = articles.map(article => ({ ...article, date: article.date.getTime() }));

                let sortableArticles = [...articles];
                if (sortConfig?.key !== null) {
                    sortableArticles.sort((a, b) => {
                        if (a[sortConfig.key] < b[sortConfig.key]) {
                            return sortConfig.direction === 'ascending' ? -1 : 1;
                        }
                        if (a[sortConfig.key] > b[sortConfig.key]) {
                            return sortConfig.direction === 'ascending' ? 1 : -1;
                        }
                        return 0;
                    });
                }
                return sortableArticles;
            },
            getFilteredArticles: () => {
                const { sortedArticles, selectedStatus, selectedType, selectedDate } = get();
                return sortedArticles.filter((article) => {
                    const matchesStatus =
                        selectedStatus === '' || article.status === parseInt(selectedStatus, 10);
                    const matchesType =
                        selectedType === '' || article.type.trim() === selectedType.trim();

                    const articleDate = new Date(article.date);
                    let selectedDateObj = selectedDate ? new Date(selectedDate) : null;

                    if (selectedDateObj) {
                        selectedDateObj.setDate(selectedDateObj.getDate() + 1);
                    }

                    const matchesDate = selectedDate === '' || (selectedDateObj &&
                        selectedDateObj.toLocaleDateString() === articleDate.toLocaleDateString());

                    return matchesStatus && matchesType && matchesDate;
                });
            },
            calculateAggregatedMetrics: (draftId) => {
                const { keywordData } = get();
                const keywords = keywordData[draftId] || [];

                if (keywords.length === 0) return {
                    totalSearchVolume: 'N/A',
                    avgDifficulty: 'N/A',
                    competitionLevels: 'N/A',
                    avgCpc: 'N/A',
                }

                const validKeywords = keywords.filter(keyword => keyword);

                const totalSearchVolume = validKeywords.reduce((sum, keyword) => sum + (keyword.search_volume_msv || 0), 0);
                const totalDifficulty = validKeywords.reduce((sum, keyword) => sum + (keyword.keyword_difficulty || 0), 0);
                const validDifficulties = validKeywords.filter(keyword => keyword.keyword_difficulty !== null)
                const avgDifficulty = validDifficulties.length > 0 ? Math.round(totalDifficulty / validDifficulties.length) : "N/A";


                const uniqueCompetitionLevels = [...new Set(validKeywords.map(keyword => keyword.competition).filter(Boolean))];
                const competitionLevels = uniqueCompetitionLevels.length > 0 ? uniqueCompetitionLevels.join(', ') : 'N/A';

                const totalCpc = validKeywords.reduce((sum, keyword) => sum + (parseFloat(keyword.cpc) || 0), 0);
                const validCpcs = validKeywords.filter(keyword => keyword.cpc !== null);
                const avgCpc = validCpcs.length > 0 ? (totalCpc / validCpcs.length).toFixed(2) : "N/A";


                return {
                    totalSearchVolume,
                    avgDifficulty,
                    competitionLevels,
                    avgCpc,
                };
            },
        }),
        {
            name: 'user-store', // unique name
        }
    )
);

export default useUserStore;
